import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquare, HelpCircle, GitBranch, ShieldCheck, Calendar } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import MentionPicker from "@/components/MentionPicker";
import AttachmentPicker from "@/components/AttachmentPicker";
import { useAuth } from "@/context/AuthContext";

const TYPES = [
  { key: "comment",  label: "Komentar",  icon: MessageSquare, color: "text-ink-muted",   ring: "ring-line" },
  { key: "question", label: "Pitanje",   icon: HelpCircle,    color: "text-ochre-ink",   ring: "ring-ochre/40" },
  { key: "decision", label: "Odluka",    icon: GitBranch,     color: "text-forest",      ring: "ring-forest/40" },
  { key: "approval", label: "Odobrenje", icon: ShieldCheck,   color: "text-forest",      ring: "ring-forest/40" },
];

const REQUIRED_LABEL = {
  question: "Ko treba da odgovori?",
  decision: "Ko treba da odluči?",
  approval: "Ko treba da odobri?",
};

/**
 * CommentComposer — default type is "Komentar" (no extra click needed).
 * Switching to a typed variant reveals a required-person picker + optional
 * due date. Typed comments must always specify who reacts.
 */
export default function CommentComposer({
  taskId,
  parentCommentId = null,
  onPosted,
  onCancel,
  compact = false,
}) {
  const { user } = useAuth();
  const [type, setType] = useState("comment");
  const [body, setBody] = useState("");
  const [requiredFrom, setRequiredFrom] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [dueDate, setDueDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const attachmentsUploading = attachments.some((a) => a.status === "uploading");
  const attachmentsHaveError = attachments.some((a) => a.status === "error");
  const attachmentsBlockSubmit = attachmentsUploading || attachmentsHaveError;

  useEffect(() => {
    // When switching to comment reset typed-only fields
    if (type === "comment") {
      setRequiredFrom([]);
      setDueDate("");
    }
  }, [type]);

  const isTyped = type !== "comment";

  const submit = async () => {
    if (!body.trim()) return toast.error("Upiši tekst");
    if (isTyped && requiredFrom.length === 0) {
      const label = REQUIRED_LABEL[type] || "Izaberi osobu";
      return toast.error(label);
    }
    if (attachmentsUploading) return toast.error("Sačekaj da se fajlovi upload-uju");
    if (attachmentsHaveError) return toast.error("Neki fajlovi imaju grešku — pokušaj ponovo ili ih ukloni");
    setSubmitting(true);
    try {
      const attachmentIds = attachments.filter((a) => a.status === "ready" && a.id).map((a) => a.id);
      const payload = {
        type,
        body: body.trim(),
        mentions,
        required_from_user_ids: isTyped ? requiredFrom : [],
        parent_comment_id: parentCommentId,
        due_date: isTyped && dueDate ? dueDate : null,
        attachments: attachmentIds,
      };
      const { data } = await api.post(`/tasks/${taskId}/comments`, payload);
      setBody("");
      setRequiredFrom([]);
      setMentions([]);
      setDueDate("");
      setType("comment");
      setAttachments([]);
      onPosted?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSubmitting(false);
  };

  return (
    <div
      data-testid="comment-composer"
      className={
        "border border-line bg-white rounded-md " +
        (compact ? "p-2.5" : "p-3") + " space-y-2.5"
      }
    >
      {/* Type pills — default is Komentar (index 0), so no extra click */}
      <div className="flex flex-wrap gap-1" role="tablist" aria-label="Tip upisa">
        {TYPES.map((t) => {
          const Icon = t.icon;
          const active = type === t.key;
          return (
            <button
              key={t.key}
              type="button"
              data-testid={`composer-type-${t.key}`}
              onClick={() => setType(t.key)}
              className={
                "inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors " +
                (active
                  ? "bg-ink text-white border-ink"
                  : "bg-white border-line text-ink-muted hover:border-ink/30")
              }
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      <Textarea
        data-testid="composer-body"
        rows={compact ? 2 : 3}
        placeholder={
          type === "comment"
            ? "Napiši komentar…"
            : type === "question"
              ? "Šta te zanima?"
              : type === "decision"
                ? "Šta treba odlučiti?"
                : "Šta treba odobriti?"
        }
        value={body}
        onChange={(e) => setBody(e.target.value)}
        className="resize-none text-sm"
      />

      {isTyped && (
        <div className="pt-1 border-t border-line/60 space-y-2.5">
          <MentionPicker
            value={requiredFrom}
            onChange={setRequiredFrom}
            excludeUserIds={[user?.id]}
            label={REQUIRED_LABEL[type]}
            single={false}
            testidPrefix="composer-required"
          />
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted font-medium">
              Do kad? (opciono)
            </label>
            <div className="relative mt-1">
              <Calendar className="w-3.5 h-3.5 absolute left-2 top-2 text-ink-muted" />
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="pl-7 h-8 text-sm"
                data-testid="composer-due-date"
              />
            </div>
          </div>
        </div>
      )}

      {type === "comment" && (
        <MentionPicker
          value={mentions}
          onChange={setMentions}
          excludeUserIds={[user?.id]}
          label="Pomeni osobu (opciono)"
          testidPrefix="composer-mentions"
        />
      )}

      <div className="flex items-center justify-between gap-2 pt-1">
        <AttachmentPicker value={attachments} onChange={setAttachments} />
        <div className="flex items-center gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="text-xs text-ink-muted hover:text-ink px-2 py-1"
              data-testid="composer-cancel"
            >
              Otkaži
            </button>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !body.trim() || attachmentsBlockSubmit}
            data-testid="composer-submit"
            className={
              "inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium " +
              (submitting || !body.trim() || attachmentsBlockSubmit
                ? "bg-line text-ink-muted cursor-not-allowed"
                : "bg-forest text-white hover:bg-forest/90")
            }
          >
            {submitting ? "Čuvam…" :
              attachmentsUploading ? "Šaljem fajlove…" :
              type === "comment" ? "Sačuvaj" :
              type === "question" ? "Postavi pitanje" :
              type === "decision" ? "Zatraži odluku" : "Zatraži odobrenje"}
          </button>
        </div>
      </div>
    </div>
  );
}
